/**
 * Public verify page — /verify/:leadId?ts=...&sig=...
 * Displays a badge that a company received from a NIS2AI assessment.
 * Distinct sky/emerald palette to feel "certified".
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, AlertTriangle, Loader2, ArrowRight, Calendar, Building2, Hash, ExternalLink } from 'lucide-react';
import useMetaTags from '../../hooks/useMetaTags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_UI = {
  critical: { tone: 'text-red-600 bg-red-50 border-red-200', label: 'CRÍTICO' },
  high:     { tone: 'text-orange-600 bg-orange-50 border-orange-200', label: 'ALTO' },
  medium:   { tone: 'text-amber-600 bg-amber-50 border-amber-200', label: 'MODERADO' },
  low:      { tone: 'text-emerald-600 bg-emerald-50 border-emerald-200', label: 'BAIXO' },
};

export default function VerifyBadge() {
  const { leadId } = useParams();
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true, valid: false, data: null, error: null });

  const ts = params.get('ts') || '';
  const sig = params.get('sig') || '';
  const embed = params.get('embed') === '1';

  useMetaTags({
    title: state.data ? `Verified · ${state.data.company} · NIS2AI` : 'Verificação NIS2AI',
    description: 'Certificação verificável do resultado de um Self-Assessment NIS2AI.',
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/verify/${leadId}`, { params: { ts, sig } });
        setState({ loading: false, valid: true, data, error: null });
      } catch (err) {
        setState({ loading: false, valid: false, data: null, error: err?.response?.data?.detail || 'Não foi possível verificar o certificado.' });
      }
    })();
  }, [leadId, ts, sig]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!state.valid) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="verify-invalid">
        <div className="max-w-md text-center">
          <div className="h-16 w-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">Certificado inválido</h1>
          <p className="text-sm text-slate-600 mt-2">{state.error}</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800">
            Voltar a NIS2AI <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  const d = state.data;
  const lvl = LEVEL_UI[d.level] || { tone: 'text-slate-600 bg-slate-100 border-slate-300', label: (d.level || '').toUpperCase() };
  const assessedDate = d.assessed_at ? new Date(d.assessed_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

  // Compact embed variant for iframe usage
  if (embed) {
    return (
      <div className="p-4 font-sans bg-white" data-testid="verify-embed">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">NIS2AI Verified</p>
            <p className="font-semibold text-slate-900 truncate">{d.company}</p>
            <p className="text-xs text-slate-500 truncate">{d.source_pretty} · {d.score}/100 · {lvl.label}</p>
          </div>
        </div>
        <a href={`${window.location.origin}/verify/${d.lead_id}?ts=${d.ts}&sig=${sig}`} className="mt-3 block text-center text-[11px] text-indigo-600 hover:text-indigo-800">
          Ver certificado completo →
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-16 px-6" data-testid="verify-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="h-20 w-20 mx-auto rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center shadow-xl shadow-emerald-500/20">
            <ShieldCheck className="h-10 w-10 text-white" />
          </div>
          <p className="mt-4 text-xs uppercase tracking-widest text-emerald-600 font-semibold" data-testid="verify-badge-label">Certificado Verificado NIS2AI</p>
          <h1 className="mt-2 text-3xl md:text-4xl font-bold text-slate-900">{d.company}</h1>
          <p className="mt-2 text-sm text-slate-500">completou um <b>{d.source_pretty}</b></p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Pontuação</p>
              <p className="mt-1 text-4xl font-bold text-slate-900" data-testid="verify-score">{d.score ?? '—'}<span className="text-lg text-slate-400 font-normal">/100</span></p>
              {d.level && (
                <span className={`mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${lvl.tone}`} data-testid="verify-level">
                  {lvl.label}
                </span>
              )}
            </div>
            {d.classification && (
              <div>
                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Classificação</p>
                <p className="mt-1 text-base font-medium text-slate-900" data-testid="verify-classification">{d.classification}</p>
              </div>
            )}
          </div>

          <hr className="my-6 border-slate-100" />

          <dl className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
              <dt className="text-slate-500 w-32 shrink-0">Data avaliação</dt>
              <dd className="text-slate-900 font-medium">{assessedDate}</dd>
            </div>
            <div className="flex items-center gap-3">
              <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
              <dt className="text-slate-500 w-32 shrink-0">Realizado por</dt>
              <dd className="text-slate-900 font-medium">{d.name || '—'}</dd>
            </div>
            <div className="flex items-center gap-3">
              <Hash className="h-4 w-4 text-slate-400 shrink-0" />
              <dt className="text-slate-500 w-32 shrink-0">Hash do resultado</dt>
              <dd className="text-slate-700 font-mono text-xs" data-testid="verify-result-hash">{d.result_hash}</dd>
            </div>
          </dl>

          <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              Certificado emitido em <span className="font-mono">{new Date(d.ts).toLocaleString('pt-PT')}</span>
            </p>
            <Link to="/" className="text-sm text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1" data-testid="verify-cta">
              Fazer o meu próprio <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-slate-500">
          Este certificado pode ser verificado a qualquer momento em <b className="text-slate-700">{window.location.origin}/verify/{d.lead_id}</b>. Qualquer alteração aos dados originais invalida a assinatura HMAC.
        </p>
        <p className="mt-3 text-center">
          <a href="https://nis2ai.pt" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
            Powered by NIS2AI <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </div>
    </div>
  );
}
